import { useEffect, useState } from "react";

import { getCachedAuthContext } from "~/components/auth";
import { env } from "~/env";
import { useAuthorization } from "~/hooks/useAuthorization";

/**
 * What Compono Assist offers the signed-in user in this organisation, as the
 * chat service decides it (`GET {CHAT_AI_URL}/entitlement`).
 *
 * The chat service is the single authority: it applies the organisation's
 * own "who can use Compono Assist" setting, and enforces the same answer on
 * every request — so the bubble shows for exactly the people whose messages
 * it will accept. The `canAccessAiChat` permission no longer decides this.
 *
 * `undefined` until known. Anything short of an answer — no chat service on
 * the stage, a failed request — is "nothing", so the bubble stays away. A
 * check that failed in a way a retry may fix (a 5xx, the network) is asked
 * once more after a pause.
 */
export interface AssistEntitlement {
  assist: boolean;
  coaching: boolean;
}

export const NOTHING: AssistEntitlement = {
  assist: false,
  coaching: false,
};

/** Before the single retry of a check that could not be answered. */
export const RETRY_AFTER_MS = 15_000;

export const fetchAssistEntitlement = async (): Promise<
  AssistEntitlement & { retry: boolean }
> => {
  const auth = getCachedAuthContext();
  const headers: Record<string, string> = {};
  const token = auth?.user?.access_token;
  if (token) headers.authorization = `Bearer ${token}`;
  if (typeof auth?.orgId === "string") {
    headers["x-organisation-id"] = auth.orgId;
  }
  try {
    const response = await fetch(`${env.CHAT_AI_URL}/entitlement`, {
      headers,
    });
    if (!response.ok) {
      // 5xx may pass; a 4xx (not a member, signed out) will not.
      return { ...NOTHING, retry: response.status >= 500 };
    }
    const body = (await response.json()) as Partial<AssistEntitlement>;
    return {
      assist: body.assist === true,
      coaching: body.coaching === true,
      retry: false,
    };
  } catch {
    return { ...NOTHING, retry: true };
  }
};

export const useAssistEntitlement = (
  enabled: boolean,
): AssistEntitlement | undefined => {
  const { orgId } = useAuthorization();
  const [entitlement, setEntitlement] = useState<AssistEntitlement>();

  useEffect(() => {
    if (!enabled || !env.CHAT_AI_URL) {
      setEntitlement(undefined);
      return undefined;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = async (retriesLeft: number) => {
      const { retry, ...answer } = await fetchAssistEntitlement();
      if (cancelled) return;
      setEntitlement(answer);
      if (retry && retriesLeft > 0) {
        timer = setTimeout(() => void check(retriesLeft - 1), RETRY_AFTER_MS);
      }
    };
    void check(1);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Asked again in a different organisation: the audience is per org.
  }, [enabled, orgId]);

  return entitlement;
};
